import * as React from "react"
import * as Yup from "yup"
import { CmsVendor } from "@modules/graphql/types"

import {
  useSanityCmsProjectsForIntegrationQuery,
  useSanityCmsDatasetsForIntegrationQuery,
} from "@modules/cms/shared/queries.generated"

import {
  RadioButtonConnectedField,
  SelectConnectedField,
} from "gatsby-interface"
import Form from "@modules/form/components/Form"
import { composeIntegrationConfig } from "../utils"
import { ConnectCmsFormProps } from "../types"
import { ConnectCmsFormControls } from "./ConnectCmsFormControls"
import { useSetCmsIntegration } from "../hooks/useSetCmsIntegration"
import { CmsDataType } from "@modules/cms/shared/utils"
import Loading from "@modules/ui/components/Loading"
import { ConnectCmsFormLayout } from "./ConnectCmsFormLayout"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import { FormattedMessage } from "@modules/locales"

type FormValues = {
  dataSourceProject: string
  dataSourceDataset: string
}

type Option = { value: string; label: string }

const validationSchema = Yup.object().shape<FormValues>({
  dataSourceProject: Yup.string().required(
    cmsIntegrationText.messages.validationSanityProjectRequired
  ),
  dataSourceDataset: Yup.string().required(
    cmsIntegrationText.messages.validationSanityDatasetRequired
  ),
})

export type ConnectSanityFormProps = ConnectCmsFormProps

export function ConnectSanityForm({
  siteId,
  siteName,
  onFetchError,
  onComplete,
  onCancel,
}: ConnectSanityFormProps) {
  const { data, loading, error } = useSanityCmsProjectsForIntegrationQuery({
    variables: {
      siteId,
    },
    onError: onFetchError,
  })
  const [setCmsIntegration] = useSetCmsIntegration({
    siteId,
  })

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading && data === undefined) {
    return (
      <Loading
        delay={1000}
        message={cmsIntegrationText.messages.loadingSanityProjects}
      />
    )
  }

  const sanityProjects = data?.sanityCMSProjectsForIntegration ?? []
  const projectOptions: Option[] = sanityProjects.map(project => ({
    value: project.id,
    label: project.name,
  }))
  let initialProjectId = sanityProjects[0]?.id ?? ``
  let initialDatasetId = ``

  const initialProject = sanityProjects.find(project => project.selected)
  if (initialProject) {
    initialProjectId = initialProject.id
    initialDatasetId = initialProject.selectedDataset ?? ``
  }

  return (
    <Form<FormValues>
      initialValues={{
        dataSourceProject: initialProjectId,
        dataSourceDataset: initialDatasetId,
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        return setCmsIntegration({
          variables: {
            config: {
              ...composeIntegrationConfig(
                CmsDataType.SanityProject,
                sanityProjects,
                values.dataSourceProject
              ),
              [CmsDataType.SanityDataset]: values.dataSourceDataset,
            },
            siteId,
            vendor: CmsVendor.Sanity,
          },
        }).finally(onComplete)
      }}
      enableReinitialize
    >
      {({ isSubmitting, values, setValues }) => {
        return (
          <ConnectCmsFormLayout
            description={
              <FormattedMessage<"siteName", "strong">
                message={cmsIntegrationText.messages.chooseSanityDataSource}
                values={{ siteName }}
                tags={{
                  strong: function(content) {
                    return <strong>{content}</strong>
                  },
                }}
              />
            }
            controls={
              <ConnectCmsFormControls
                isSubmitting={isSubmitting}
                onCancel={onCancel}
              />
            }
          >
            {projectOptions.length > 1 && (
              <RadioButtonConnectedField
                name="dataSourceProject"
                label={cmsIntegrationText.labels.sanityProject}
                options={projectOptions}
                variant="framed"
                onChange={e => {
                  setValues(
                    {
                      dataSourceProject: e.target.value,
                      dataSourceDataset: ``,
                    },
                    false
                  )
                }}
              />
            )}
            <SanityDatasetField
              name="dataSourceDataset"
              label={cmsIntegrationText.labels.sanityDataset}
              siteId={siteId}
              selectedProject={values.dataSourceProject}
            />
          </ConnectCmsFormLayout>
        )
      }}
    </Form>
  )
}

const EMPTY_DATA_SET_OPTION: Option = {
  value: ``,
  label: cmsIntegrationText.labels.sanityDatasetPlaceholder,
}

type SanityDatasetFieldProps = {
  name: string
  label: React.ReactNode
  siteId: string
  selectedProject: string
}

function SanityDatasetField({
  name,
  label,
  siteId,
  selectedProject,
}: SanityDatasetFieldProps) {
  const { data, loading, error } = useSanityCmsDatasetsForIntegrationQuery({
    variables: {
      siteId,
      projectId: selectedProject,
    },
    skip: !selectedProject,
  })

  const projectDatasets = data?.sanityCMSDatasetsForIntegration ?? []

  let options: Option[] = [EMPTY_DATA_SET_OPTION]
  if (selectedProject) {
    options = options.concat(
      projectDatasets.map(dataset => ({
        label: dataset.name,
        value: dataset.name,
      }))
    )
  }

  return (
    <React.Fragment>
      <SelectConnectedField
        name={name}
        label={label}
        options={options}
        disabled={loading}
      />
      {error && <ErrorAlert>{error.message}</ErrorAlert>}
    </React.Fragment>
  )
}
